/* eslint-disable */
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Formik, Form, Field } from 'formik';
import { Button } from 'primereact/button';
import * as Yup from 'yup';
import gql from 'graphql-tag';
import { useSelector, useDispatch } from 'react-redux';
import { Dropdown } from 'primereact/dropdown';
import { useHistory, useLocation } from 'react-router-dom';
import { growlErrorsWithMessage } from '../utils/NotificationUtil';
import { NotificationContext } from '../components/GrowlNotifications';
import { requestNewToken, updateLocale } from '../services/auth/auth-network';
import runAcessTokenChecker from '../services/auth/auth-scheduler';
import { LegacyInputTextComponent, LegacyInputPasswordComponent } from '../components/prime-formik';
import apollo from '../services/apollo';
import { useLocale as locale } from '../reducers/locale';
import { getViewerLocale, saveViewerLocale } from '../services/locale/ViewerLocale';
import { persistCredentialsSession } from '../services/auth/auth-storage';
import LockedOut from '../components/LockedOut';
import useLockoutMechanism from '../hooks/useLockoutMechanism';

const compare = (a, b) => {
  if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
  if (b.label.toLowerCase() > a.label.toLowerCase()) return -1;
  return 0;
};

const LOCALES_QUERY = gql`
  query SupportLocales {
    locales: activeLocales
  }
`;

const i18n = defineMessages({
  usernameRequired: { id: 'login.username_required', defaultMessage: 'Username is required' },
  passwordRequired: {
    id: 'login.password_required',
    defaultMessage: 'Password is required',
  },
  languageDropdownPlaceholder: { id: 'login.language_dropdown.placeholder', defaultMessage: '-- Choose a language --' },
  invalidCredentials: { id: 'login.error', defaultMessage: 'Invalid username or password' },
});

const Login = (props) => {
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [defaultLanguage, setDefaultLanguage] = useState(getViewerLocale() || 'en-US');

  const { lockedOut, timeUntilLockoutExpiration, lockOut } = useLockoutMechanism();

  const {
    cldr: { localeDisplayName },
  } = useSelector((state) => state.i18n);

  const dispatch = useDispatch();

  const history = useHistory();

  const { formatMessage } = useIntl();

  const growl = useContext(NotificationContext);

  const { pathname } = useLocation();

  const updateMessages = (selectedLanguage) => dispatch(locale(selectedLanguage));

  const getSupportedLanguages = async () => {
    const result = await apollo.query({
      query: LOCALES_QUERY,
    });

    if (result && result.data) {
      const availableSupportedLanguages = result.data.locales.map((lang) => ({
        value: lang,
        label: localeDisplayName(lang),
      }));

      setSupportedLanguages(availableSupportedLanguages);
    }
  };

  useEffect(() => {
    getSupportedLanguages();
  }, []);

  const generateToken = async (values, resetForm) => {
    const { username, password } = values;

    try {
      const result = await requestNewToken({ username, password });

      if (result && result.data && result.data.generateToken.token && result.data.generateToken.refreshToken) {
        const { token, refreshToken } = result.data.generateToken;
        persistCredentialsSession(token, refreshToken);

        runAcessTokenChecker();

        updateLocale({ username, preferredLanguage: defaultLanguage });

        saveViewerLocale(defaultLanguage);

        updateMessages(defaultLanguage);

        history.push(pathname);
      }

      if (
        result &&
        result.data &&
        result.data.generateToken.userId &&
        !result.data.generateToken.isMultiFactorAuthentication
      ) {
        const { userId } = result.data.generateToken;

        history.push(`/email-verification?userId=${userId}`);
      }

      if (
        result &&
        result.data &&
        result.data.generateToken.userId &&
        result.data.generateToken.isMultiFactorAuthentication
      ) {
        const { userId } = result.data.generateToken;

        updateLocale({ username, preferredLanguage: defaultLanguage });

        saveViewerLocale(defaultLanguage);

        updateMessages(defaultLanguage);

        history.push(`/multi-factor-confirmation?userId=${userId}`);
      }

      if (result && result.data && result.data.generateToken.error) {
        const { error } = result.data.generateToken;

        const { type, lockedOutTimestamp } = error;

        resetForm();

        if (type === 'INVALID_CREDENTIALS') {
          growlErrorsWithMessage(growl, formatMessage(i18n.invalidCredentials));
        }

        if (type === 'LOCKED_OUT') {
          lockOut(lockedOutTimestamp);

          growlErrorsWithMessage(growl, formatMessage(i18n.lockedOut));
        }
      }
    } catch (err) {
      console.log(err);

      resetForm();
    }
  };

  const initialValues = {
    username: '',
    password: '',
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string().required(formatMessage(i18n.usernameRequired)),
    password: Yup.string().required(formatMessage(i18n.passwordRequired)),
  });

  const sortedOptions = supportedLanguages.sort(compare);

  const placeholder = formatMessage(i18n.languageDropdownPlaceholder);

  if (lockedOut && timeUntilLockoutExpiration) {
    return <LockedOut timeUntilLockoutExpiration={timeUntilLockoutExpiration} />;
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, formikBag) => {
        const { resetForm } = formikBag;

        generateToken(values, resetForm);
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Form onSubmit={handleSubmit}>
          <div className="p-grid p-fluid p-justify-center logo-top">
            <img src="images/default-195.png" className="brand center-block" alt="GPI Logo" />
          </div>
          <br />
          <div className="p-grid p-fluid p-justify-center">
            <div className="p-col-12 p-md-2">
              <Dropdown
                value={defaultLanguage}
                options={sortedOptions}
                filter
                filterBy="label"
                placeholder={placeholder}
                onChange={(e) => {
                  const { value } = e;

                  setDefaultLanguage(value);
                  saveViewerLocale(value);
                  updateMessages(value);
                }}
              />
            </div>
          </div>
          <br />
          <div className="p-grid p-fluid p-justify-center">
            <div className="p-col-12 p-md-2">
              <FormattedMessage id="login.username" defaultMessage="Username">
                {(text) => (
                  <Field id="username" name="username" component={LegacyInputTextComponent} placeholder={text} />
                )}
              </FormattedMessage>
            </div>
          </div>
          <br />
          <div className="p-grid p-fluid p-justify-center">
            <div className="p-col-12 p-md-2">
              <FormattedMessage id="login.password" defaultMessage="Password">
                {(text) => (
                  <Field id="password" name="password" component={LegacyInputPasswordComponent} placeholder={text} />
                )}
              </FormattedMessage>
            </div>
          </div>
          <br />
          <div className="p-grid p-justify-center">
            <div className="p-col-6 p-md-2">
              <FormattedMessage id="login.button" defaultMessage="Login">
                {(text) => <Button type="submit" label={text} disabled={isSubmitting} />}
              </FormattedMessage>
              <FormattedMessage id="login.forgot_password" defaultMessage="Forgot password">
                {(text) => (
                  <Button
                    type="button"
                    label={text}
                    className="p-button-secondary"
                    onClick={() => history.push('/forgot-password')}
                  />
                )}
              </FormattedMessage>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default Login;
